import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Component, ChangeDetectionStrategy, Inject } from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';

import { StaticMessage } from '@shared/enums';
import { DprOnlineApproval, DprOnlineApprover } from '@shared/models/dpr';
import { SubprojectConfigurationCustomNames } from '@shared/models/project-setup';
import { ApproveStatus } from '@shared/enums/dpr';
import { filter, Observable, of, tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BaseDialogComponent } from '@shared/models/component';
import { ConfirmDialogComponent } from '@shared/components';
import { Constants, getStaticMessage } from '@shared/utils';
import { canDeactivateUnsavedChanges } from '@shared/guards';

@UntilDestroy()
@Component({
  selector: 'app-dpr-online-approve-modal',
  templateUrl: './dpr-online-approve-modal.component.html',
  styleUrls: ['./dpr-online-approve-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprOnlineApproveModalComponent extends BaseDialogComponent {
  staticMessage: typeof StaticMessage = StaticMessage;
  ApproveStatus: typeof ApproveStatus = ApproveStatus;
  onlineApprovalForm = new FormGroup({
    approveStatus: new FormControl<ApproveStatus | null>(
      null,
      Validators.required
    ),
    comment: new FormControl<string | null>(null),
  });
  constructor(
    protected dialogRef: MatDialogRef<DprOnlineApproveModalComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      dprOnlineApprover: DprOnlineApprover;
      customNames: SubprojectConfigurationCustomNames;
      dprDetailId: number | string;
      title: string;
    }
  ) {
    super(dialogRef);

    this.onlineApprovalForm.patchValue({
      ...this.onlineApprovalForm.value,
      approveStatus:
        this.data.dprOnlineApprover?.dprOnlineApproval?.approveStatus,
      comment: this.data.dprOnlineApprover?.dprOnlineApproval?.comment,
    });
  }

  get onlineApproval(): DprOnlineApproval {
    return this.data?.dprOnlineApprover?.dprOnlineApproval;
  }

  get isCurrentUser(): boolean {
    return this.data?.dprOnlineApprover?.isCurrentUser;
  }

  get canApprove(): boolean {
    return this.data?.dprOnlineApprover?.canApprove;
  }

  get approveStatus(): ApproveStatus {
    return this.onlineApprovalForm.controls.approveStatus.value;
  }

  canDeactivate(): boolean | Observable<boolean> {
    return !this.onlineApprovalForm.dirty;
  }

  save(): Observable<boolean> {
    this.onSave();
    return of(false);
  }

  onSave(): void {
    if (this.onlineApprovalForm.dirty) {
      if (this.onlineApproval?.approveStatus) {
        this.dialogRef.close({
          id: this.onlineApproval?.id,
          dprDetailId: this.data.dprDetailId,
          accountId: this.data?.dprOnlineApprover?.accountId,
          approveStatus: this.onlineApprovalForm.value?.approveStatus,
          comment: this.onlineApprovalForm.value?.comment,
        });
      } else {
        const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
          ...Constants.DialogOptions,
          data: {
            title: $localize`:@@dpr.dprOnlineApproveModal.titleConfirmStatus:Are you sure you want to set this status?`,
            message: $localize`:@@dpr.dprOnlineApproveModal.txtSaveStatus:You won't be able to change it on this revision. Press 'Cancel' to stay on the page or 'Save' to save the status.`,
            buttonLabel: getStaticMessage(StaticMessage.SaveButton),
            buttonIcon: 'check',
            cancelButtonLabel: getStaticMessage(StaticMessage.CancelButton),
          },
        });

        confirmDialog
          .afterClosed()
          .pipe(
            untilDestroyed(this),
            filter((result) => !!result),
            tap(() => {
              this.dialogRef.close({
                id: this.onlineApproval?.id,
                dprDetailId: this.data.dprDetailId,
                accountId: this.data?.dprOnlineApprover?.accountId,
                approveStatus: this.onlineApprovalForm.value?.approveStatus,
                comment: this.onlineApprovalForm.value?.comment,
              });
            })
          )
          .subscribe();
      }
    }
  }

  onCancel(): void {
    if (!this.onlineApprovalForm.dirty) {
      this.dialogRef.close(null);
      return;
    }

    canDeactivateUnsavedChanges(this, this._dialog)
      .pipe(
        untilDestroyed(this),
        filter((val) => !!val),
        tap(() => {
          this.dialogRef.close(null);
        })
      )
      .subscribe();
  }
}
